/**
 * Auditor Routes Configuration
 *
 * Defines all routes for auditor user pages.
 */

import type { ReactElement } from 'react';
import { BarChart3, ClipboardList, FileText, AlertTriangle, Award, Settings } from 'lucide-react';

// Auditor Pages
import { AuditorDashboardPage } from '../pages/auditor/AuditorDashboardPage';
import { AuditorApplicationQueuePage } from '../pages/auditor/AuditorApplicationQueuePage';
import { AuditorApplicationReviewPage } from '../pages/auditor/AuditorApplicationReviewPage';
import { AuditChecklistListPage } from '../pages/auditor/AuditChecklistListPage';
import { OfficialAuditChecklistPage } from '../pages/auditor/OfficialAuditChecklistPage';
import { NCRListPage } from '../pages/auditor/NCRListPage';
import { NCRPage } from '../pages/auditor/NCRPage';
import { AuditorCertificateListPage } from '../pages/auditor/AuditorCertificateListPage';
import { AuditorSettingsPage } from '../pages/auditor/AuditorSettingsPage';

/**
 * Route definition interface
 */
export interface RouteDefinition {
  path: string;
  element: ReactElement;
  label: string;
  icon?: ReactElement;
  showInNav?: boolean;
  children?: RouteDefinition[];
}

/**
 * Auditor routes configuration
 */
export const auditorRoutes: RouteDefinition[] = [
  {
    path: '/auditor/dashboard',
    element: <AuditorDashboardPage />,
    label: 'Dashboard',
    icon: <BarChart3 className="w-5 h-5" />,
    showInNav: true,
  },
  {
    path: '/auditor/applications',
    element: <AuditorApplicationQueuePage />,
    label: 'Applications',
    icon: <ClipboardList className="w-5 h-5" />,
    showInNav: true,
    children: [
      {
        path: '/auditor/applications/:id',
        element: <AuditorApplicationReviewPage />,
        label: 'Application Review',
        showInNav: false,
      },
    ],
  },
  {
    path: '/auditor/audits',
    element: <AuditChecklistListPage />,
    label: 'Audit Checklists',
    icon: <FileText className="w-5 h-5" />,
    showInNav: true,
    children: [
      {
        path: '/auditor/audits/:id',
        element: <OfficialAuditChecklistPage />,
        label: 'Official Audit Checklist',
        showInNav: false,
      },
    ],
  },
  {
    path: '/auditor/ncr',
    element: <NCRListPage />,
    label: 'Non-Conformance Reports',
    icon: <AlertTriangle className="w-5 h-5" />,
    showInNav: true,
    children: [
      {
        path: '/auditor/ncr/create',
        element: <NCRPage />,
        label: 'Create NCR',
        showInNav: false,
      },
      {
        path: '/auditor/ncr/:id',
        element: <NCRPage />,
        label: 'NCR Details',
        showInNav: false,
      },
    ],
  },
  {
    path: '/auditor/certificates',
    element: <AuditorCertificateListPage />,
    label: 'Certificates',
    icon: <Award className="w-5 h-5" />,
    showInNav: true,
    children: [
      {
        path: '/auditor/certificates/:id',
        element: <AuditorCertificateListPage />,
        label: 'Certificate Details',
        showInNav: false,
      },
    ],
  },
  {
    path: '/auditor/settings',
    element: <AuditorSettingsPage />,
    label: 'Settings',
    icon: <Settings className="w-5 h-5" />,
    showInNav: true,
  },
];

/**
 * Get all routes (flattened)
 */
export function getAllAuditorRoutes(): RouteDefinition[] {
  const routes: RouteDefinition[] = [];

  function flattenRoutes(routeList: RouteDefinition[]) {
    routeList.forEach((route) => {
      routes.push(route);
      if (route.children) {
        flattenRoutes(route.children);
      }
    });
  }

  flattenRoutes(auditorRoutes);
  return routes;
}

/**
 * Get navigation items (routes shown in sidebar)
 */
export function getAuditorNavigationItems(): RouteDefinition[] {
  return auditorRoutes.filter((route) => route.showInNav);
}

/**
 * Get route by path
 */
export function getAuditorRouteByPath(path: string): RouteDefinition | undefined {
  const allRoutes = getAllAuditorRoutes();
  return allRoutes.find((route) => route.path === path);
}

export default auditorRoutes;